import { zodResolver } from '@hookform/resolvers/zod';
import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, unwrap } from '../../../api/client';
import { ImageEditor } from '../../../components/images';
import { useNotify } from '../../../lib/notify';
import { idempotency, keys, type Staff } from '../api';
import { applyFieldErrors } from '../lib';
import { FieldGrid, PasswordField } from '../ui';
import { AttachmentsPanel } from '../../attachments';
import { RolePicker } from './RolePicker';
import { PersonAvatar } from './TeamTab';

/** A readable temporary password, e.g. `Kite-Plum-4827`. */
export function temporaryPassword(random: () => number = Math.random): string {
  const words = ['Amber', 'Brick', 'Cedar', 'Delta', 'Ember', 'Frost', 'Grove', 'Harbor', 'Indigo'];
  const more = ['Jade', 'Kite', 'Lotus', 'Maple', 'Noble', 'Opal', 'Plum', 'Quartz', 'River'];
  const pick = (list: string[]) => list[Math.floor(random() * list.length)] ?? list[0] ?? 'Shop';
  const digits = String(1000 + Math.floor(random() * 9000));
  return `${pick(words)}-${pick(more)}-${digits}`;
}

const contact = {
  email: z.union([z.literal(''), z.email('Enter a valid email').max(254)]),
  phone: z
    .string()
    .trim()
    .max(40)
    .refine((v) => v === '' || /^\+?[0-9 ()-]{6,}$/.test(v), 'Enter a valid phone number'),
};

export const staffSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name').max(200),
    ...contact,
    password: z.string(),
    roleIds: z.array(z.string()).min(1, 'Pick at least one role'),
    creating: z.boolean(),
  })
  .refine((v) => v.email || v.phone, {
    message: 'Enter an email or a phone number to sign in with',
    path: ['email'],
  })
  .refine((v) => !v.creating || v.password.length >= 8, {
    message: 'At least 8 characters',
    path: ['password'],
  });
type Values = z.infer<typeof staffSchema>;
const FIELDS = ['name', 'email', 'phone', 'password', 'roleIds'] as const;

function conflictField(e: unknown): 'email' | 'phone' | null {
  if (!(e instanceof ApiError)) return null;
  if (e.code === 'EMAIL_TAKEN') return 'email';
  if (e.code === 'PHONE_TAKEN') return 'phone';
  return null;
}

/** Add a staff account with a temporary password, or edit details and roles of one. */
export function StaffDialog({ staff, onClose }: { staff: Staff | null; onClose: () => void }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const qc = useQueryClient();
  const notify = useNotify();
  const creating = staff === null;
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(staffSchema),
    defaultValues: {
      name: staff?.name ?? '',
      email: staff?.email ?? '',
      phone: staff?.phone ?? '',
      password: creating ? temporaryPassword() : '',
      roleIds: staff?.roles.map((r) => r.id) ?? [],
      creating,
    },
  });

  const onSubmit = handleSubmit(async (v) => {
    try {
      if (creating) {
        await unwrap(
          api.POST('/staff', {
            params: idempotency(),
            body: {
              name: v.name.trim(),
              password: v.password,
              roleIds: v.roleIds,
              ...(v.email ? { email: v.email } : {}),
              ...(v.phone ? { phone: v.phone.trim() } : {}),
            },
          }),
        );
        notify.success(`${v.name} can now sign in with the temporary password`);
      } else {
        let version = staff.version;
        const detailsChanged =
          v.name.trim() !== staff.name ||
          (v.email || null) !== staff.email ||
          (v.phone.trim() || null) !== staff.phone;
        if (detailsChanged) {
          const updated = await unwrap(
            api.PATCH('/staff/{id}', {
              params: { path: { id: staff.id } },
              body: {
                name: v.name.trim(),
                email: v.email || null,
                phone: v.phone.trim() || null,
                version,
              },
            }),
          );
          version = updated.version;
        }
        const before = staff.roles
          .map((r) => r.id)
          .sort()
          .join();
        if ([...v.roleIds].sort().join() !== before) {
          await unwrap(
            api.PUT('/staff/{id}/roles', {
              params: { path: { id: staff.id } },
              body: { roleIds: v.roleIds, version },
            }),
          );
        }
        notify.success('Staff member updated');
      }
      await qc.invalidateQueries({ queryKey: keys.staff });
      void qc.invalidateQueries({ queryKey: keys.subscriptionStatus });
      await qc.invalidateQueries({ queryKey: keys.roles });
      onClose();
    } catch (e) {
      const field = conflictField(e);
      if (field) {
        setError(field, { message: `Another account already uses this ${field}` });
        return;
      }
      if (e instanceof ApiError && e.code === 'LAST_OWNER_PROTECTED') {
        setError('roleIds', { message: 'The last owner must keep the Owner role' });
        return;
      }
      if (!applyFieldErrors(e, FIELDS, setError)) notify.error(e);
    }
  });

  const password = watch('password');

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={phone}
    >
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', gap: 2, alignItems: 'center' }}>
          {!creating && (
            <ImageEditor
              target={{ kind: 'staff', id: staff.id }}
              url={staff.imageUrl}
              canEdit
              label={staff.name}
            >
              <PersonAvatar name={staff.name} imageUrl={staff.imageUrl} size={56} />
            </ImageEditor>
          )}
          <span>
            {creating ? 'Add a staff account' : `Edit ${staff.name}`}
            <Typography variant="body2" color="text.secondary">
              {creating
                ? 'They sign in with a temporary password and choose their own on first sign-in.'
                : 'Update contact details, photo and what they can do.'}
            </Typography>
          </span>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Full name"
              autoFocus
              {...register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
            />
            <FieldGrid>
              <TextField
                label="Email"
                type="email"
                {...register('email')}
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
              />
              <TextField
                label="Phone"
                type="tel"
                placeholder="+92 300 1234567"
                {...register('phone')}
                error={Boolean(errors.phone)}
                helperText={errors.phone?.message}
              />
            </FieldGrid>
            {creating && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                <PasswordField
                  label="Temporary password"
                  {...register('password')}
                  strength={password}
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message ?? 'Share it with them privately.'}
                  sx={{ flex: 1 }}
                />
                <Tooltip title="Generate another">
                  <IconButton
                    aria-label="Generate password"
                    onClick={() =>
                      setValue('password', temporaryPassword(), { shouldValidate: true })
                    }
                    sx={{ mt: 0.25, border: 1, borderColor: 'divider' }}
                  >
                    <AutorenewRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
            <div>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Roles
              </Typography>
              <Controller
                control={control}
                name="roleIds"
                render={({ field }) => (
                  <RolePicker
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.roleIds?.message}
                  />
                )}
              />
            </div>
            {staff && (
              <AttachmentsPanel
                recordType="STAFF"
                recordId={staff.id}
                sx={{ boxShadow: 'none', border: 1, borderColor: 'divider' }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={isSubmitting}>
            {creating ? 'Create account' : 'Save changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const resetSchema = z.object({ newPassword: z.string().min(8, 'At least 8 characters').max(128) });

export function ResetPasswordDialog({
  staff,
  isSelf = false,
  onClose,
}: {
  staff: Staff;
  /** Resetting your own password: you sign in again with it (no forced change). */
  isSelf?: boolean;
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ newPassword: string }>({
    resolver: zodResolver(resetSchema),
    defaultValues: { newPassword: temporaryPassword() },
  });
  const onSubmit = handleSubmit(async (v) => {
    try {
      await unwrap(
        api.POST('/staff/{id}/reset-password', {
          params: { path: { id: staff.id } },
          body: v,
        }),
      );
      await qc.invalidateQueries({ queryKey: keys.staff });
      void qc.invalidateQueries({ queryKey: keys.subscriptionStatus });
      notify.success(
        isSelf
          ? 'Password changed — sign in again with your new password'
          : `Password reset — ${staff.name} was signed out everywhere`,
      );
      onClose();
    } catch (e) {
      if (!applyFieldErrors(e, ['newPassword'], setError)) notify.error(e);
    }
  });
  return (
    <Dialog open onClose={isSubmitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <DialogTitle sx={{ fontWeight: 700 }}>Reset password</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">
              {isSelf
                ? 'You will be signed out of every device and will sign in again with this new password.'
                : `${staff.name} will be signed out of every device and must choose a new password at next sign-in.`}
            </Alert>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <PasswordField
                label={isSelf ? 'New password' : 'Temporary password'}
                {...register('newPassword')}
                strength={watch('newPassword')}
                error={Boolean(errors.newPassword)}
                helperText={errors.newPassword?.message}
                sx={{ flex: 1 }}
              />
              <Tooltip title="Generate another">
                <IconButton
                  aria-label="Generate password"
                  onClick={() =>
                    setValue('newPassword', temporaryPassword(), { shouldValidate: true })
                  }
                  sx={{ mt: 0.25, border: 1, borderColor: 'divider' }}
                >
                  <AutorenewRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="warning" loading={isSubmitting}>
            Reset password
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
